import React, { useEffect, useState, useRef } from "react";
import { useSelector } from "react-redux";
import {
  Container,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { notification } from "../../utils/Notifications";
import {
  getColletedLots,
  getLots,
  getLotsBySuperadmin,
  getColletedLotsBySuperadmin,
} from "../../api/lotsApi.js";
import { getZones } from "../../api/zonesApi";
import { getDeliveryMen, getDeliveryMenBySuperadmin } from "../../api/userApi";
import LotsTabBar from "../../components/Lots/LotsTabBar";
import Title from "../../components/SharedComponents/Title";
import SearchBar from "../../components/SharedComponents/SearchBar";
import LotsAssingUserDialog from "../../components/Lots/LotsAssingUserDialog/index.js";
import useLogout from "../../hooks/auth/authHook.js";
import styles from "./lotsStyles.js";
import { UserTag } from "iconsax-react";
import { usePermissions } from "../../hooks/permissions/usePermissions.js";

const Lots = () => {
  const [logout] = useLogout();
  const { hasAllPermissions } = usePermissions();
  const { extraInformation } = useSelector((state) => state.user);
  const isSuperAdmin = extraInformation?.id_role === 5;

  const [lots, setLots] = useState([]);
  const [collectedLots, setCollectedLots] = useState([]);
  const [selectedLots, setSelectedLots] = useState([]);
  const [zones, setZones] = useState([]);
  const [selectedZone, setSelectedZone] = useState(null); // null = "Todas las zonas"
  const [deliverers, setDeliverers] = useState([]);
  const [search, setSearch] = useState("");
  const [hasItemsSelected, setHasItemsSelected] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(null);
  const hasFetchedInitial = useRef(false);
  const hasFetchedDeliverersInitial = useRef(false);

  const handleDialogOpen = (dialogName) => {
    setOpenDialog(dialogName);
  };

  const handleDialogClose = () => {
    setOpenDialog(null);
  };

  const fetchLots = () => {
    setIsLoading(true);

    const fetchFunction = isSuperAdmin ? getLotsBySuperadmin : getLots;
    const fetchParams = isSuperAdmin
      ? selectedZone
        ? [Number(selectedZone)]
        : null
      : undefined;

    fetchFunction(fetchParams)
      .then((response) => {
        if (response.data.status_Code === 200) {
          setLots(response.data.collect_batch || []);
        } else if (response.data.status_Code === 401) {
          notification({
            text: "Sesión expirada. Por favor, inicia sesión nuevamente.",
            type: "error",
          });
          logout();
        } else {
          notification({
            text: "Error al obtener los lotes",
            type: "error",
          });
        }
      })
      .catch(() => {
        notification({
          text: "Error de conexión, revisa tu conexión a internet",
          type: "error",
        });
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const fetchCollectedLots = () => {
    setIsLoading(true);

    const fetchFunction = isSuperAdmin
      ? getColletedLotsBySuperadmin
      : getColletedLots;
    const fetchParams = isSuperAdmin
      ? selectedZone
        ? [Number(selectedZone)]
        : null
      : undefined;

    fetchFunction(fetchParams)
      .then((response) => {
        if (response.data.status_Code === 200) {
          setCollectedLots(response.data.assigned_collected_batches || []);
        } else if (response.data.status_Code === 401) {
          notification({
            text: "Sesión expirada. Por favor, inicia sesión nuevamente.",
            type: "error",
          });
          logout();
        } else {
          notification({
            text: "Error al obtener los lotes",
            type: "error",
          });
        }
      })
      .catch(() => {
        notification({
          text: "Error de conexión, revisa tu conexión a internet",
          type: "error",
        });
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const fetchZones = () => {
    getZones()
      .then((response) => {
        if (response.data.status_Code === 200) {
          setZones(response.data.ls_zones || []);
        } else if (response.data.status_Code === 401) {
          notification({
            text: "Sesión expirada. Por favor, inicia sesión nuevamente.",
            type: "error",
          });
          logout();
        } else {
          notification({
            text: "Error al obtener las zonas",
            type: "error",
          });
        }
      })
      .catch(() => {
        notification({
          text: "Error de conexión, revisa tu conexión a internet",
          type: "error",
        });
      });
  };

  const handleZoneChange = (event) => {
    const zoneValue = event.target.value;
    setSelectedZone(zoneValue === "" ? null : zoneValue);
  };

  const fetchDeliverers = () => {
    const fetchFunction = isSuperAdmin
      ? getDeliveryMenBySuperadmin
      : getDeliveryMen;
    const fetchParams = isSuperAdmin
      ? undefined
      : { validatePermissions: "false", filterActive: "true" };

    fetchFunction(fetchParams)
      .then((response) => {
        if (response.data.status_Code === 200) {
          setDeliverers(response.data.users || []);
        } else if (response.data.status_Code === 401) {
          notification({
            text: "Sesión expirada. Por favor, inicia sesión nuevamente.",
            type: "error",
          });
          logout();
        } else {
          notification({
            text: "Error al obtener los repartidores",
            type: "error",
          });
        }
      })
      .catch(() => {
        notification({
          text: "Error de conexión, revisa tu conexión a internet",
          type: "error",
        });
      });
  };

  useEffect(() => {
    fetchZones();
  }, []);

  useEffect(() => {
    // Solo hacer fetch si ya tenemos el rol del usuario y no hemos hecho fetch antes
    if (
      extraInformation?.id_role !== undefined &&
      !hasFetchedDeliverersInitial.current
    ) {
      hasFetchedDeliverersInitial.current = true;
      fetchDeliverers();
    }
  }, [extraInformation?.id_role]);

  useEffect(() => {
    if (extraInformation?.id_role !== undefined && !hasFetchedInitial.current) {
      hasFetchedInitial.current = true;

      if (hasAllPermissions(["vw_pel"])) {
        fetchLots();
      }

      if (hasAllPermissions(["vw_asl"])) {
        fetchCollectedLots();
      }
    }
  }, [extraInformation?.id_role]);

  useEffect(() => {
    if (
      isSuperAdmin &&
      extraInformation?.id_role === 5 &&
      hasFetchedInitial.current
    ) {
      if (hasAllPermissions(["vw_pel"])) {
        fetchLots();
      }

      if (hasAllPermissions(["vw_asl"])) {
        fetchCollectedLots();
      }
    }
  }, [selectedZone]);

  return (
    <Container style={styles.mainContainer}>
      <Title title="Recolección" />
      <SearchBar setSearch={setSearch}>
        {hasAllPermissions(["col_asl", "lot_vw", "vw_pel"]) && (
          <Button
            variant="contained"
            disabled={!hasItemsSelected}
            startIcon={<UserTag variant="Bold" />}
            onClick={() => handleDialogOpen("AssingUserLots")}
            sx={hasItemsSelected ? styles.activeButton : styles.inactiveButton}
          >
            Asignar lotes
          </Button>
        )}
        <LotsAssingUserDialog
          setLots={setLots}
          open={openDialog === "AssingUserLots"}
          onClose={handleDialogClose}
          onOpenDialog={handleDialogOpen}
          selectedLots={selectedLots}
          deliverers={deliverers}
          fetchCollectedLots={fetchCollectedLots}
        />
      </SearchBar>

      {isSuperAdmin && (
        <Container style={{ marginBottom: "16px", marginTop: "16px" }}>
          <FormControl variant="standard" style={{ minWidth: 200 }}>
            <InputLabel id="zone-filter-label">Filtrar por zona</InputLabel>
            <Select
              labelId="zone-filter-label"
              id="zone-filter"
              value={selectedZone || ""}
              onChange={handleZoneChange}
            >
              <MenuItem value="">
                <em>Todas las zonas</em>
              </MenuItem>
              {zones.map((zone) => {
                const zoneId =
                  typeof zone === "string"
                    ? zone
                    : zone.id_zone || zone.id || zone.zone_code;
                const zoneName =
                  typeof zone === "string"
                    ? zone
                    : zone.zone_name || zone.name || zone.zone_code || zone.id;
                return (
                  <MenuItem key={zoneId} value={zoneId}>
                    {zoneName}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        </Container>
      )}

      <LotsTabBar
        lots={lots}
        collectedLots={collectedLots}
        fetchLots={fetchLots}
        fetchCollectedLots={fetchCollectedLots}
        isLoading={isLoading}
        search={search}
        setHasItemsSelected={setHasItemsSelected}
        setSelectedLots={setSelectedLots}
      />
    </Container>
  );
};

export default Lots;
